import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getConsent, saveConsent, applyConsent, CONSENT_EVENT, OPEN_PREFS_EVENT } from '@/lib/consent';

export const openCookiePreferences = () => window.dispatchEvent(new Event(OPEN_PREFS_EVENT));

const Toggle = ({ checked, disabled, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    data-testid={testid}
    className="relative inline-flex items-center rounded-full transition-colors"
    style={{
      width: 44, height: 24,
      background: checked ? 'var(--accent-primary)' : 'var(--bg-subtle)',
      opacity: disabled ? 0.7 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
      border: '1px solid var(--border-color)',
    }}
  >
    <span
      className="inline-block rounded-full transition-transform"
      style={{ width: 18, height: 18, background: '#fff', transform: checked ? 'translateX(22px)' : 'translateX(3px)' }}
    />
  </button>
);

const CookieConsent = ({ wide = false }) => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (consent) {
      // Returning visitor: load whatever they already consented to.
      applyConsent(consent);
      setAnalytics(!!consent.analytics);
      setMarketing(!!consent.marketing);
    } else {
      const p = window.location.pathname;
      const suppressed = p.startsWith('/messages') || p.startsWith('/inbox');
      if (!suppressed) setShowBanner(true);
    }
    const openPrefs = () => {
      const c = getConsent();
      setAnalytics(!!(c && c.analytics));
      setMarketing(!!(c && c.marketing));
      setShowPrefs(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, openPrefs);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, openPrefs);
  }, []);

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setShowBanner(false);
    setShowPrefs(false);
  };

  const rejectAll = () => {
    saveConsent({ analytics: false, marketing: false });
    setShowBanner(false);
    setShowPrefs(false);
  };

  const savePreferences = () => {
    saveConsent({ analytics, marketing });
    setShowBanner(false);
    setShowPrefs(false);
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            data-testid="cookie-consent-banner"
            className="fixed bottom-0 left-0 right-0 z-[9998] px-4 sm:px-6 py-4"
            style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border-color)' }}
          >
            <div className={`${wide ? 'chrome-wide ' : ''}max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-center gap-4`}>
              <p className="text-sm flex-1" style={{ color: 'var(--text-secondary)' }}>
                {t('cookies.bannerText')}
              </p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <button
                  onClick={rejectAll}
                  data-testid="cookie-reject-all"
                  className="px-4 py-2 rounded-full text-sm font-medium transition-colors"
                  style={{ background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  {t('cookies.rejectAll')}
                </button>
                <button
                  onClick={() => setShowPrefs(true)}
                  data-testid="cookie-manage-preferences"
                  className="px-4 py-2 rounded-full text-sm font-medium transition-colors"
                  style={{ background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  {t('cookies.managePreferences')}
                </button>
                <button
                  onClick={acceptAll}
                  data-testid="cookie-accept-all"
                  className="px-5 py-2 rounded-full text-sm font-semibold transition-transform hover:-translate-y-0.5"
                  style={{ background: 'var(--accent-primary)', color: '#fff' }}
                >
                  {t('cookies.acceptAll')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPrefs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            data-testid="cookie-preferences-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setShowPrefs(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              data-testid="cookie-preferences-modal"
              className="w-full max-w-lg rounded-2xl p-6 sm:p-8"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{t('cookies.prefsTitle')}</h2>
                <button onClick={() => setShowPrefs(false)} data-testid="cookie-prefs-close" style={{ color: 'var(--text-secondary)' }}>
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('cookies.essentialTitle')}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{t('cookies.essentialDesc')}</p>
                  </div>
                  <span className="text-xs whitespace-nowrap px-2 py-1 rounded-full" data-testid="cookie-essential-locked"
                    style={{ color: 'var(--accent-primary)', background: 'var(--bg-subtle)' }}>{t('cookies.alwaysOn')}</span>
                </div>

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('cookies.analyticsTitle')}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{t('cookies.analyticsDesc')}</p>
                  </div>
                  <Toggle checked={analytics} onChange={setAnalytics} testid="cookie-toggle-analytics" />
                </div>

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('cookies.marketingTitle')}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{t('cookies.marketingDesc')}</p>
                  </div>
                  <Toggle checked={marketing} onChange={setMarketing} testid="cookie-toggle-marketing" />
                </div>
              </div>

              <div className="mt-8 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowPrefs(false)}
                  data-testid="cookie-prefs-cancel"
                  className="px-4 py-2 rounded-full text-sm font-medium"
                  style={{ background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  {t('cookies.cancel')}
                </button>
                <button
                  onClick={savePreferences}
                  data-testid="cookie-prefs-save"
                  className="px-5 py-2 rounded-full text-sm font-semibold transition-transform hover:-translate-y-0.5"
                  style={{ background: 'var(--accent-primary)', color: '#fff' }}
                >
                  {t('cookies.confirm')}
                </button>
              </div>
              <p className="mt-5 text-xs text-center" style={{ color: 'var(--text-secondary)' }}>
                <a href="/privacy" className="underline">{t('cookies.privacyPolicy')}</a>{' · '}
                <a href="/terms" className="underline">{t('cookies.termsOfService')}</a>{' · '}
                <a href="/cookies" className="underline">{t('cookies.cookiePolicy')}</a>
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsent;
